import { useMemo, useState, type CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { fetchForecastMonthly } from '@/api/client'
import type { ForecastMonthAmount, ForecastMonthMetric } from '@/api/types'
import { EmptyState } from '@/components/EmptyState'
import { ErrorState } from '@/components/ErrorState'
import { HubPage } from '@/components/HubPage'
import { LoadingState } from '@/components/LoadingState'
import { PageHelp } from '@/components/PageHelp'
import { Money } from '@/components/Money'
import { MonthMetricToggle } from '@/components/MonthStrip'
import { MONTH_STRIP_LABELS, monthMetricAmount } from '@/lib/month-strip'
import { RevenueTrend } from '@/components/RevenueTrend'
import { useAppChrome } from '@/context/AppChromeContext'
import { formatMoney } from '@/lib/format-money'
import {
  formatMomChangeLabel,
  monthOverMonthChangeRatio,
  monthOverMonthTintStyle,
} from '@/lib/month-over-month-tint'
import {
  filterAmountsForQuarter,
  monthsForQuarter,
  quarterMonthLabels,
  sumMetricForQuarter,
} from '@/lib/quarter'

function monthAmountSeries(
  months: ForecastMonthAmount[] | undefined,
  metric: ForecastMonthMetric,
): number[] {
  const byMonth = new Map((months ?? []).map((row) => [row.month, row]))
  return MONTH_STRIP_LABELS.map((_, index) => monthMetricAmount(byMonth.get(index + 1), metric))
}

function customerYearTotal(
  customer: {
    year_committed: number | string
    year_weighted: number | string
    year_upside: number | string
  },
  metric: ForecastMonthMetric,
): number {
  const raw =
    metric === 'committed'
      ? customer.year_committed
      : metric === 'weighted'
        ? customer.year_weighted
        : customer.year_upside
  const numeric = typeof raw === 'string' ? Number(raw) : Number(raw)
  return Number.isFinite(numeric) ? numeric : 0
}

function MonthAmountCell({
  amount,
  previous,
  currency,
}: {
  amount: number
  previous: number | null
  currency: string
}) {
  const changeRatio = monthOverMonthChangeRatio(amount, previous)
  const tintStyle = monthOverMonthTintStyle(changeRatio)
  const momLabel = formatMomChangeLabel(changeRatio)
  const blank = !(amount > 0)
  const title = blank
    ? (momLabel ?? undefined)
    : [formatMoney(amount, currency), momLabel].filter(Boolean).join(' · ')

  const style: CSSProperties | undefined = tintStyle

  return (
    <td
      className={`table__num monthly-forecast__month-col${
        tintStyle ? ' monthly-forecast__month-col--tinted' : ''
      }${blank ? ' monthly-forecast__month-col--blank' : ''}`}
      style={style}
      title={title}
    >
      {blank ? null : <Money amount={amount} currency={currency} />}
    </td>
  )
}

function YearTotalCell({ amount, currency }: { amount: number; currency: string }) {
  if (!(amount > 0)) {
    return (
      <td className="table__num monthly-forecast__year-col monthly-forecast__year-col--blank" />
    )
  }
  return (
    <td className="table__num monthly-forecast__year-col">
      <Money amount={amount} currency={currency} />
    </td>
  )
}

export function MonthlyForecastPage() {
  const { year, viewCode, quarter } = useAppChrome()
  const [metric, setMetric] = useState<ForecastMonthMetric>('weighted')

  const monthlyQuery = useQuery({
    queryKey: ['forecast-monthly', year, viewCode],
    queryFn: () => fetchForecastMonthly(year, viewCode),
  })

  const data = monthlyQuery.data
  const currency = data?.currency ?? 'USD'

  const visibleMonths = useMemo(() => monthsForQuarter(quarter), [quarter])
  const visibleLabels = useMemo(() => quarterMonthLabels(quarter), [quarter])

  const portfolioAmounts = useMemo(
    () => filterAmountsForQuarter(monthAmountSeries(data?.months, metric), quarter),
    [data?.months, metric, quarter],
  )

  const portfolioYearTotal = useMemo(
    () => portfolioAmounts.reduce((sum, value) => sum + value, 0),
    [portfolioAmounts],
  )

  const customersWithRevenue = useMemo(() => {
    if (!data) return []
    return data.customers.filter((customer) => {
      if (quarter === 'all') return customerYearTotal(customer, metric) > 0
      return sumMetricForQuarter(customer.months, metric, quarter) > 0
    })
  }, [data, metric, quarter])

  return (
    <HubPage
      className="monthly-forecast"
      chrome={
        <header className="page__header monthly-forecast__header">
          <div className="page__header-lead">
            <div>
              <h1 className="page__title">Monthly forecast</h1>
              <p className="page__subtitle">
                Customer × month rollup for {year}
                {quarter === 'all' ? '' : ` · Q${quarter}`}. Switch metric to compare Committed,
                Weighted, or Upside. Cell tint shows change vs the prior month. Edit lines on{' '}
                <Link to="/opportunities">Opportunities</Link>.
              </p>
            </div>
            <PageHelp />
          </div>
          <MonthMetricToggle value={metric} onChange={setMetric} idPrefix="forecast-metric" />
        </header>
      }
    >
      {monthlyQuery.isLoading && (
        <LoadingState variant="skeleton-block" label="Loading monthly forecast…" />
      )}

      {monthlyQuery.isError && (
        <ErrorState
          message="Could not load monthly forecast."
          onRetry={() => void monthlyQuery.refetch()}
        />
      )}

      {data && customersWithRevenue.length === 0 && (
        <EmptyState title="No forecast revenue for this year yet." />
      )}

      {data && customersWithRevenue.length > 0 && (
        <div className="table-pane table-pane--fill monthly-forecast__table-pane">
          <div className="table-pane__scroll">
            <table className="table table--dense monthly-forecast__table">
              <caption className="visually-hidden">
                {metric} monthly forecast by customer for {year}. Green cells rose vs prior month;
                red cells fell. Zero amounts are blank.
              </caption>
              <colgroup>
                <col className="monthly-forecast__col-customer" />
                <col className="monthly-forecast__col-trend" />
                {visibleLabels.map((label) => (
                  <col key={label} className="monthly-forecast__col-month" />
                ))}
                <col className="monthly-forecast__col-year" />
              </colgroup>
              <thead className="table__head--sticky">
                <tr>
                  <th scope="col" className="table__sticky-col monthly-forecast__customer-col">
                    Customer
                  </th>
                  <th scope="col" className="monthly-forecast__trend-col">
                    Trend
                  </th>
                  {visibleLabels.map((label) => (
                    <th key={label} scope="col" className="table__num monthly-forecast__month-col">
                      {label}
                    </th>
                  ))}
                  <th scope="col" className="table__num monthly-forecast__year-col">
                    {quarter === 'all' ? 'Year' : `Q${quarter}`}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="monthly-forecast__portfolio-row">
                  <th scope="row" className="table__sticky-col monthly-forecast__customer-col">
                    All customers
                    <span className="monthly-forecast__meta">
                      {data.opportunity_count} line{data.opportunity_count === 1 ? '' : 's'}
                    </span>
                  </th>
                  <td className="monthly-forecast__trend-col">
                    <RevenueTrend
                      amounts={portfolioAmounts}
                      currency={currency}
                      density="compact"
                      showBadge={false}
                      aria-label={`Portfolio ${metric} trend for ${year}`}
                    />
                  </td>
                  {portfolioAmounts.map((amount, index) => (
                    <MonthAmountCell
                      key={visibleLabels[index] ?? visibleMonths[index]}
                      amount={amount}
                      previous={index === 0 ? null : (portfolioAmounts[index - 1] ?? 0)}
                      currency={currency}
                    />
                  ))}
                  <YearTotalCell amount={portfolioYearTotal} currency={currency} />
                </tr>
                {customersWithRevenue.map((customer) => {
                  const amounts = filterAmountsForQuarter(
                    monthAmountSeries(customer.months, metric),
                    quarter,
                  )
                  const yearTotal =
                    quarter === 'all'
                      ? customerYearTotal(customer, metric)
                      : sumMetricForQuarter(customer.months, metric, quarter)
                  return (
                    <tr key={customer.customer_id}>
                      <th
                        scope="row"
                        className="table__sticky-col monthly-forecast__customer-col"
                        title={`View opportunities for ${customer.customer_name}`}
                      >
                        <Link
                          className="monthly-forecast__customer-link"
                          to={`/opportunities?customer_id=${encodeURIComponent(customer.customer_id)}&customer_name=${encodeURIComponent(customer.customer_name)}`}
                        >
                          {customer.customer_name}
                        </Link>
                      </th>
                      <td className="monthly-forecast__trend-col">
                        <RevenueTrend
                          amounts={amounts}
                          currency={currency}
                          density="compact"
                          showBadge={false}
                          aria-label={`${customer.customer_name} ${metric} trend for ${year}`}
                        />
                      </td>
                      {amounts.map((amount, index) => (
                        <MonthAmountCell
                          key={visibleLabels[index] ?? visibleMonths[index]}
                          amount={amount}
                          previous={index === 0 ? null : (amounts[index - 1] ?? 0)}
                          currency={currency}
                        />
                      ))}
                      <YearTotalCell amount={yearTotal} currency={currency} />
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </HubPage>
  )
}
